import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Grid, TextField } from '@material-ui/core';
import { OnwardChip } from '@/styles/blocks';
import { TabRow } from '@/components/ShipmentForm/blocks';
import { MAX_PIN_LABEL_LENGTH, appendPinLabel, removePinLabel } from './pinLabels.utils';

// Chips sit in the input's startAdornment, which is where MUI v4's Autocomplete
// puts its own renderTags output — so this field matches the Tags field above it
// without reaching for an Autocomplete it has no options to populate.
const chipLayout = css`
    .MuiOutlinedInput-root {
        flex-wrap: wrap;
        padding: 6px 9px;
    }

    .MuiOutlinedInput-root > .MuiChip-root {
        margin: 3px;
    }

    .MuiOutlinedInput-input {
        padding: 9px 4px;
        width: 0;
        min-width: 140px;
        flex-grow: 1;
    }
`;

/**
 * Chip input for the order's `pin_labels`, shown under its pin on the dispatch plan map.
 * A label commits on Enter or blur.
 *
 * @param {object} props
 * @param {object} props.order The order being edited; reads `pin_labels`.
 * @param {{ modifyOrder: Function }} props.callbacks Modal callbacks.
 */
const PinLabels = ({ order, callbacks }) => {
    const [draft, setDraft] = useState('');
    const labels = order.pin_labels || [];

    const commit = () => {
        const next = appendPinLabel(labels, draft);
        setDraft('');

        // appendPinLabel hands back the same array for an empty or duplicate
        // entry. Skipping modifyOrder then keeps a stray blur from marking the
        // modal dirty.
        if (next !== labels) {
            callbacks.modifyOrder({ pin_labels: next });
        }
    };

    return (
        <TabRow>
            <Grid item xs={12}>
                <TextField
                    // MUI v4 only wires the label to the input via htmlFor when
                    // it is given an id; without one the field is unlabelled to
                    // screen readers.
                    id="pin-labels"
                    variant="outlined"
                    fullWidth
                    label="Pin Labels"
                    placeholder={labels.length ? '' : 'Add a label'}
                    value={draft}
                    css={chipLayout}
                    helperText={`Rendered under this order's pin on the dispatch plan map. Press Enter to add. Max ${MAX_PIN_LABEL_LENGTH} characters each.`}
                    inputProps={{ maxLength: MAX_PIN_LABEL_LENGTH }}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            // Otherwise the keypress submits the surrounding form.
                            e.preventDefault();
                            commit();
                        }
                    }}
                    InputProps={{
                        startAdornment: labels.map((label, index) => (
                            <OnwardChip
                                // Labels are de-duplicated case-insensitively, so
                                // the text itself is a stable unique key.
                                key={label}
                                label={label}
                                onDelete={() => callbacks.modifyOrder({ pin_labels: removePinLabel(labels, index) })}
                            />
                        )),
                    }}
                />
            </Grid>
        </TabRow>
    );
};

export default PinLabels;
